import React, { useMemo } from 'react';

const DashboardTab = ({ data, t }) => {
    if (!data) return null;

    const fallback = t('receptionOutpatients.details.fallback.na', { defaultValue: 'N/A' });

    const petAgeDisplay = useMemo(() => {
        const raw = data?.pet?.age;
        if (raw === null || raw === undefined || raw === '') return fallback;

        const toYears = (n) => t('common.ageYears', { count: n, defaultValue: `${n} years` });
        const toMonths = (n) => t('common.ageMonths', { count: n, defaultValue: `${n} months` });

        if (typeof raw === 'number' && Number.isFinite(raw)) return toYears(Math.max(0, Math.round(raw)));

        const s = String(raw).trim();
        const n = parseFloat(s.replace(/[^\d.]/g, ''));
        if (Number.isNaN(n)) return s || fallback;

        const lower = s.toLowerCase();
        const hasYear = lower.includes('year') || lower.includes('years');
        const hasMonth = lower.includes('month') || lower.includes('months');

        if (hasYear) return toYears(Math.max(0, Math.round(n)));
        if (hasMonth) return toMonths(Math.max(0, Math.round(n)));

        // Fallback assumption: numeric age means years
        return toYears(Math.max(0, Math.round(n)));
    }, [data?.pet?.age, fallback, t]);

    const petGenderDisplay = useMemo(() => {
        const raw = data?.pet?.gender;
        if (raw === null || raw === undefined || raw === '') return fallback;
        const s = String(raw).trim();
        const k = s.toLowerCase();
        const normalized = (k === 'm' || k === 'male') ? 'male' : (k === 'f' || k === 'female') ? 'female' : null;
        if (!normalized) return s;
        return t(`completeProfile.gender.options.${normalized}`, { defaultValue: s });
    }, [data?.pet?.gender, fallback, t]);

    const petGenderIcon = useMemo(() => {
        const k = String(data?.pet?.gender || '').trim().toLowerCase();
        if (k === 'm' || k === 'male') return 'mars';
        if (k === 'f' || k === 'female') return 'venus';
        return 'venus-mars';
    }, [data?.pet?.gender]);

    return (
        <div className="dashboard-tab-content">

            <div className="info-cards-container">
                {/* Row 1 */}
                <div className="info-card-premium">
                    <div className="card-icon"><i className="fas fa-hashtag"></i></div>
                    <div className="card-details">
                        <label>{t('receptionOutpatients.details.labels.serialNumber', { defaultValue: 'Serial Number' })}</label>
                        <p>{data?.out_serial_num || fallback}</p>
                    </div>
                </div>
                <div className="info-card-premium">
                    <div className="card-icon"><i className="fas fa-calendar-check"></i></div>
                    <div className="card-details">
                        <label>{t('receptionOutpatients.details.labels.visitDate', { defaultValue: 'Visit Date' })}</label>
                        <p>{data?.visit_date ? data.visit_date.split(' ')[0] : fallback}</p>
                    </div>
                </div>

                {/* Row 2 */}
                <div className="info-card-premium">
                    <div className="card-icon"><i className="fas fa-user-tie"></i></div>
                    <div className="card-details">
                        <label>{t('receptionOutpatients.details.labels.petGuardian', { defaultValue: 'Pet Guardian' })}</label>
                        <p>{data?.pet_owner?.full_name || fallback}</p>
                    </div>
                </div>
                <div className="info-card-premium">
                    <div className="card-icon"><i className="fas fa-paw"></i></div>
                    <div className="card-details">
                        <label>{t('receptionOutpatients.details.labels.petName', { defaultValue: 'Pet Name' })}</label>
                        <p>{data?.pet?.name || fallback}</p>
                    </div>
                </div>

                {/* Row 3 */}
                <div className="info-card-premium">
                    <div className="card-icon"><i className="fas fa-dna"></i></div>
                    <div className="card-details">
                        <label>{t('receptionOutpatients.details.labels.species', { defaultValue: 'Species' })}</label>
                        <p>{data?.pet?.species?.name || fallback}</p>
                    </div>
                </div>
                <div className="info-card-premium">
                    <div className="card-icon"><i className="fas fa-fingerprint"></i></div>
                    <div className="card-details">
                        <label>{t('receptionOutpatients.details.labels.breed', { defaultValue: 'Breed' })}</label>
                        <p>{data?.pet?.breed?.name || fallback}</p>
                    </div>
                </div>

                {/* Row 4 */}
                <div className="info-card-premium">
                    <div className="card-icon"><i className="fas fa-birthday-cake"></i></div>
                    <div className="card-details">
                        <label>{t('common.age', { defaultValue: 'Age' })}</label>
                        <p>{petAgeDisplay}</p>
                    </div>
                </div>
                <div className="info-card-premium">
                    <div className="card-icon"><i className={`fas fa-${petGenderIcon}`}></i></div>
                    <div className="card-details">
                        <label>{t('receptionOutpatients.details.labels.gender', { defaultValue: 'Gender' })}</label>
                        <p>{petGenderDisplay}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardTab;
